/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PRODUCT_TYPE } from 'configs/constants';
import { checkFreeTrialEligibility } from 'services/api/subscriptionApi';
import { TFreeTrialState, TProductType } from 'typings';

export const fetchFreeTrialEligibility = createAsyncThunk(
  'users/fetchFreeTrialEligibility',
  async (
    payload: {
      productType: TProductType;
    },
    thunkAPI
  ) => {
    try {
      const response = await checkFreeTrialEligibility(payload.productType);
      const responseData = response.data;
      return responseData;
    } catch (error) {
      console.error('Error in fetchFreeTrialEligibility:', error);
      return thunkAPI.rejectWithValue(
        'Failed to fetch free trial eligibility details'
      );
    }
  }
);

const initialTrialState: TFreeTrialState = {
  isEligibleForFreeTrialHomeSecurity: false,
  isEligibleForFreeTrialBird: false,
  isLoading: false
};

export const freeTrialEligibilitySlice = createSlice({
  name: 'freeTrialEligibility',
  initialState: initialTrialState,
  reducers: {
    updateFreeTrialEligibility: (
      state,
      action: PayloadAction<{
        productType?: TProductType;
        isFreeTrialEligible: boolean;
      }>
    ) => {
      const productType =
        action.payload.productType || PRODUCT_TYPE.HOME_SECURITY; // Default to HomeSecurity if product type is not provided
      switch (productType) {
        case PRODUCT_TYPE.BIRD:
          state.isEligibleForFreeTrialBird = action.payload.isFreeTrialEligible;
          break;
        case PRODUCT_TYPE.HOME_SECURITY:
        default:
          state.isEligibleForFreeTrialHomeSecurity =
            action.payload.isFreeTrialEligible;
          break;
      }
    },
    clearFreeTrialEligibility: (state) => {
      state.isEligibleForFreeTrialHomeSecurity = false;
      state.isEligibleForFreeTrialBird = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFreeTrialEligibility.fulfilled, (state, action) => {
        state.isLoading = false;
        const { productType } = action.meta.arg || {
          productType: PRODUCT_TYPE.HOME_SECURITY
        };
        switch (productType) {
          case PRODUCT_TYPE.BIRD:
            state.isEligibleForFreeTrialBird = action.payload.trial;
            break;
          case PRODUCT_TYPE.HOME_SECURITY:
          default:
            state.isEligibleForFreeTrialHomeSecurity = action.payload.trial;
            break;
        }
      })
      .addCase(fetchFreeTrialEligibility.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFreeTrialEligibility.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

// Action creators are generated for each case reducer function

export const { updateFreeTrialEligibility, clearFreeTrialEligibility } =
  freeTrialEligibilitySlice.actions;

export default freeTrialEligibilitySlice.reducer;
